import { z } from 'zod';
import { personnelDateSchema } from './details.js';
/**
 * WHY SOMEONE IS INACTIVE OR SEPARATED, and WHEN a status change took effect (#772; Julio, #582 decisions
 * 4, 5 and 6).
 *
 * These are values HR enters, not derived status (CLAUDE.md rule 4): the lifecycle's states and allowed
 * moves are unchanged. "On leave" is a reason under Inactive and not a new status (decision 4).
 *
 * Both lists are closed and written once here. IDS are stored, never labels (the same rule as
 * `candidateSourceSchema`). `other` appears in both and means a different thing in each, which is why the
 * two lists are checked per state below rather than as one union.
 */
export const INACTIVE_REASONS = [
    { id: 'leave', label: 'On leave' },
    { id: 'suspension', label: 'Suspension' },
    { id: 'other', label: 'Other' },
];
export const SEPARATION_REASONS = [
    { id: 'resigned', label: 'Resigned' },
    { id: 'let-go', label: 'Let go' },
    { id: 'contract-ended', label: 'Contract ended' },
    { id: 'other', label: 'Other' },
];
/** Every code either list can hold. A stored or sent code is also checked against the list for the state it belongs to. */
export const statusReasonSchema = z.enum(['leave', 'suspension', 'resigned', 'let-go', 'contract-ended', 'other']);
export const statusReasonLabels = {
    leave: 'On leave',
    suspension: 'Suspension',
    resigned: 'Resigned',
    'let-go': 'Let go',
    'contract-ended': 'Contract ended',
    other: 'Other',
};
/** The reasons HR may pick when moving to `state`, in the order the picker draws them; empty for a state that carries none. */
export function reasonChoicesFor(state) {
    if (state === 'inactive')
        return INACTIVE_REASONS;
    if (state === 'separated')
        return SEPARATION_REASONS;
    return [];
}
/** What the picker is called for each state, so the screen and the messages say the same words. */
export const reasonQuestions = {
    inactive: 'Why are they inactive?',
    separated: 'Why are they leaving?',
};
/** Longest optional note HR may add to a reason. */
export const MAX_STATUS_NOTE_LENGTH = 500;
/**
 * A date HR enters for when a status change took effect: a calendar day, in the agency's own terms, so
 * it keeps the day typed and acquires no zone (`personnelDateSchema`). Kept beside the time the change
 * was recorded, never instead of it.
 */
export const effectiveOnSchema = personnelDateSchema;
/**
 * What the person record holds about their CURRENT status change: the reason (Inactive and Separated
 * only), the optional note, and the optional effective date. Replaced by every status change, so it always
 * describes the latest one. The earlier ones stay in the history, one audit row each.
 *
 * Strict, and every part nullable, so a change with nothing to hold writes `null` rather than a half-empty
 * object, and a record from before this existed (no key at all) still parses.
 */
export const lifecycleDetailSchema = z.object({
    reason: statusReasonSchema.nullable().default(null),
    note: z.string().trim().min(1).max(MAX_STATUS_NOTE_LENGTH).nullable().default(null),
    effectiveOn: effectiveOnSchema.nullable().default(null),
}).strict();
/**
 * What an audit row records about the same change: the reason's CODE and the effective date, never the
 * note. The note is free text and can hold personal detail, so it stays on the person record only: for a
 * move that carries a reason code the lifecycle route writes the code's LABEL as the row's `reason` and
 * ignores whatever else the client sent there. A code and a day name no person.
 */
export const lifecycleAuditDetailSchema = z.object({
    reason: statusReasonSchema.nullable().default(null),
    effectiveOn: effectiveOnSchema.nullable().default(null),
}).strict();
/**
 * The reason, note and date a status change may carry, judged against the state it moves to.
 *
 *  - Inactive and Separated REQUIRE a reason from their own list (Julio: HR picks it when someone becomes
 *    inactive or leaves);
 *  - any other state carries no reason and no note, so an Active person cannot be left holding "Resigned";
 *  - the effective date is optional on every move.
 *
 * Returns the issues as sentences for a person to read; empty means the combination is allowed.
 */
export function statusDetailIssues(to, detail) {
    const choices = reasonChoicesFor(to);
    if (choices.length === 0) {
        return detail.statusReason !== undefined || detail.statusNote !== undefined
            ? ['A reason and a note are only for a move to Inactive or Separated.']
            : [];
    }
    if (detail.statusReason === undefined)
        return [`Choose a reason: ${reasonQuestions[to]}`];
    return choices.some((choice) => choice.id === detail.statusReason) ? [] : [`That reason is not one of the choices for ${to === 'inactive' ? 'Inactive' : 'Separated'}.`];
}
//# sourceMappingURL=status-reasons.js.map